import React, { useEffect, useMemo, useRef } from "react";
import * as echarts from "echarts";
import dayjs from "dayjs";
import { useTheme } from "@mui/material/styles";

const formatUtcToBrowserDate = (time) => {
  if (!time) return null;

  const normalizedTime =
    typeof time === "string" && !time.endsWith("Z") ? `${time}Z` : time;

  return dayjs(normalizedTime);
};

const WiredCongestionLevelChart = ({
  data = [],
  wiredCongestionApiChartData,
}) => {
  const theme = useTheme();
  const chartRef = useRef(null);
  const chartInstanceRef = useRef(null);

  const chartData = useMemo(() => {
    const series = Array.isArray(data) ? data : (data?.series ?? []);
    const dataMap = new Map();

    series.forEach((item) => {
      if (!Array.isArray(item)) return;

      const [time, value] = item;
      const browserTime = formatUtcToBrowserDate(time);

      if (!browserTime?.isValid()) return;

      const key = browserTime.format("YYYY-MM-DD HH");
      dataMap.set(key, Number(value) || 0);
    });

    const lastTime =
      series.length > 0
        ? formatUtcToBrowserDate(series[series.length - 1]?.[0])
        : dayjs().startOf("hour");

    const endTime =
      lastTime?.isValid() === true
        ? lastTime.startOf("hour")
        : dayjs().startOf("hour");

    const startTime = endTime.subtract(23, "hour");

    return Array.from({ length: 24 }, (_, index) => {
      const currentTime = startTime.add(index, "hour");
      const key = currentTime.format("YYYY-MM-DD HH");

      return {
        label: currentTime.format("HH:mm"),
        fullTime: currentTime.format("YYYY/MM/DD HH:mm"),
        value: dataMap.get(key) ?? 0,
      };
    });
  }, [data]);

  const dynamicMax = useMemo(() => {
    if (!chartData || chartData.length === 0) {
      return 10;
    }

    const values = chartData.map((item) => item.value);
    const maxValue = Math.max(...values, 0);

    const threshold = Number(wiredCongestionApiChartData?.threshold) || 10;
    return Math.max(maxValue, threshold) || 10;
  }, [chartData, wiredCongestionApiChartData?.threshold]);

  const option = useMemo(() => {
    const xAxisData = chartData.map((item) => item.label);

    return {
      animation: false,
      backgroundColor: theme.palette.primary.white,
      grid: {
        top: 20,
        right: 24,
        bottom: 36,
        left: 42,
        containLabel: true,
      },
      tooltip: {
        trigger: "axis",
        backgroundColor: theme.palette.primary.blackTransparency,
        borderWidth: 0,
        textStyle: {
          color: theme.palette.primary.white,
          fontSize: theme.typography.customFontSize[0],
        },
        formatter: (params) => {
          const point = params?.[0];

          if (!point) return "";

          const currentData = chartData[point.dataIndex] || {};

          return `${
            currentData.fullTime || point.axisValue
          }<br/>Congested Ports: ${point.data}`;
        },
        axisPointer: {
          type: "line",
          lineStyle: {
            color: theme.palette.primary.greyThread,
            width: 1,
            type: "dashed",
          },
        },
      },
      xAxis: {
        type: "category",
        boundaryGap: false,
        data: xAxisData,
        axisLabel: {
          color: theme.palette.primary.greyFourth,
          fontSize: theme.typography.customFontSize[0],
          margin: 12,
          interval: 0,
          formatter: (value, index) => {
            const isLast = index === xAxisData.length - 1;
            return index % 4 === 0 || isLast ? value : "";
          },
        },
        axisLine: {
          show: true,
          lineStyle: {
            color: theme.palette.primary.greyThread,
            width: 1,
          },
        },
        axisTick: {
          show: false,
        },
        splitLine: {
          show: true,
          interval: 0,
          lineStyle: {
            color: theme.palette.primary.greyThread,
            width: 1,
            type: "dashed",
          },
        },
      },
      yAxis: {
        type: "value",
        min: 0,
        max: dynamicMax,
        minInterval: 1,
        splitNumber: 5,
        axisLabel: {
          color: theme.palette.primary.greyFourth,
          fontSize: theme.typography.customFontSize[0],
          margin: 12,
        },
        axisLine: {
          show: false,
        },
        axisTick: {
          show: false,
        },
        splitLine: {
          show: true,
          lineStyle: {
            color: theme.palette.primary.greyThread,
            width: 1,
            type: "dashed",
          },
        },
      },
      series: [
        {
          name: "Congested Ports",
          type: "line",
          data: chartData.map((item) => item.value),
          smooth: false,
          showSymbol: false,
          symbol: "none",
          connectNulls: true,
          lineStyle: {
            color: theme.palette.primary.main,
            width: 2.2,
          },
        },
      ],
    };
  }, [chartData, theme, dynamicMax]);

  useEffect(() => {
    if (!chartRef.current) return;

    const chart =
      echarts.getInstanceByDom(chartRef.current) ||
      echarts.init(chartRef.current);

    chartInstanceRef.current = chart;
    chart.setOption(option, true);
    chart.resize();

    const handleResize = () => chart.resize();
    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);

      if (chartInstanceRef.current) {
        chartInstanceRef.current.dispose();
        chartInstanceRef.current = null;
      }
    };
  }, [option]);

  return (
    <div
      ref={chartRef}
      style={{
        width: "100%",
        minWidth: 0,
        height: "320px",
      }}
    />
  );
};

export default WiredCongestionLevelChart;
